import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatearFecha, formatearFechaHora, type LecturaMedidor, type Medidor, type Pagina } from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { ErroresFormulario } from '../componentes/piezas.js';
import { GraficoMediciones } from '../componentes/grafico-mediciones.js';
import { useSesion } from '../servicios/sesion.js';
import { useTiposActivo, SelectorFecha, SelectorLista } from '../componentes/selectores.js';
import {
  Aviso,
  Cargando,
  Dialogo,
  DialogoConfirmar,
  Etiqueta,
  Indicador,
  Vacio,
  type AdjuntoImagen,
} from '../componentes/piezas.js';
import { IconoEditar, IconoEliminar } from '../componentes/iconos.js';
import { BotonEscanearQr } from '../componentes/camara.js';
import { useEsMovil } from '../servicios/responsive.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';

/** AAAA-MM-DD en la zona horaria local, no UTC — `toISOString()` correría la fecha un día. */
function hoyLiteral(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Activo {
  id: string;
  numero: number;
  nombre: string;
  ubicacionNombre: string | null;
}

function Dato({ titulo, valor }: { titulo: string; valor: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="dato-titulo">{titulo}</div>
      <div className="dato-valor">{valor}</div>
    </div>
  );
}

/**
 * Medidores — §11.
 *
 * El caso de uso que lo motiva: una máquina de depilación IPL/láser mide más
 * "potencia" a medida que su cabezal se desgasta (menos vello bloquea el haz),
 * así que una lectura aislada no dice nada — es la pendiente entre lecturas la
 * que avisa que toca cambiar el cabezal. Por eso el centro de la pantalla es
 * el gráfico de tendencia, no una tabla de valores.
 *
 * La elección de activos vive en un modal aparte, no en un panel fijo al
 * costado: con cientos de activos por tenant, tener el listado completo
 * siempre en pantalla —incluso colapsado a una columna en móvil— empuja los
 * gráficos que sí importan fuera de la vista. El modal se abre, se elige, se
 * cierra, y lo único que queda en pantalla son los gráficos elegidos.
 */
export function Medidores(): JSX.Element {
  const { puede } = useSesion();
  const escribe = puede('medidor:escribir');

  // Todo lo que define la vista vive en la URL (tipo, activos escaneados, activo
  // abierto y medidor elegido): así, al volver de la ficha de un activo, la
  // pantalla queda igual que se dejó.
  const [parametros, setParametros] = useSearchParams();
  const tipoActivoId = parametros.get('tipo') ?? '';
  const idsEscaneados = (parametros.get('activos') ?? '').split(',').filter(Boolean);
  const abiertoId = parametros.get('abierto');
  const medidorId = parametros.get('medidor');
  const [creandoParaTipo, setCreandoParaTipo] = useState(false);

  function cambiarParametros(cambios: Record<string, string | null>): void {
    setParametros(
      (p) => {
        for (const [clave, valor] of Object.entries(cambios)) {
          if (valor) p.set(clave, valor);
          else p.delete(clave);
        }
        return p;
      },
      { replace: true },
    );
  }

  const { data: tipos } = useTiposActivo();

  // Al elegir un tipo, sus activos se cargan de una vez. Los escaneados por QR
  // se suman a esa lista.
  const {
    data: delTipo,
    isLoading: cargandoTipo,
    isError: errorTipo,
  } = useQuery({
    queryKey: ['medidores-activos-tipo', tipoActivoId],
    queryFn: () => activosDelTipo(tipoActivoId),
    enabled: tipoActivoId !== '',
  });
  // Escaneados por QR: se piden las fichas (número y ubicación para la tabla).
  const escaneados = useQueries({
    queries: idsEscaneados.map((id) => ({
      queryKey: ['activo', id],
      queryFn: () => api.get<Activo>(`/activos/${id}`),
    })),
  });
  const seleccionados = escaneados.flatMap((q) => (q.data ? [q.data] : []));

  const activos = [
    ...(delTipo ?? []),
    ...seleccionados.filter((s) => !(delTipo ?? []).some((d) => d.id === s.id)),
  ];

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Medidores</h1>
          <p className="cabecera-subtitulo">Mediciones por activo a lo largo del tiempo — la tendencia avisa el desgaste antes que la falla.</p>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div
          className="panel-cuerpo filtros"
          style={{ gap: 12, marginBottom: 0 }}
        >
          <div style={{ minWidth: 220, flex: 1 }}>
            <SelectorLista
              id="medidores-tipo"
              etiqueta="Tipo de activo"
              valor={tipoActivoId}
              alCambiar={(id) => {
                // Cambiar el tipo de activo vacía la elección: los activos ya
                // elegidos pueden no encajar más con el filtro nuevo, y dejar
                // gráficos de un tipo distinto en pantalla confundiría más de
                // lo que ayudaría.
                cambiarParametros({ tipo: id || null, activos: null, abierto: null, medidor: null });
              }}
              opciones={(tipos ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
              textoVacio="Todos los tipos"
            />
          </div>
          {tipoActivoId && escribe ? (
            <button type="button" className="boton secundario" onClick={() => setCreandoParaTipo(true)}>
              + Medidor para todo el tipo
            </button>
          ) : null}
          <BotonEscanearQr
            etiqueta="Escanear QR de activo"
            entidades={['ACTIVO']}
            alResolver={(r) => {
              if (!idsEscaneados.includes(r.entidadId)) {
                cambiarParametros({ activos: [...idsEscaneados, r.entidadId].join(',') });
              }
            }}
          />
        </div>
      </div>


      {tipoActivoId && cargandoTipo ? <Cargando /> : null}
      {tipoActivoId && errorTipo ? (
        <Aviso tono="error">No se pudieron cargar los activos de este tipo.</Aviso>
      ) : null}

      {activos.length > 0 ? (
        <TablaActivosMedidores
          activos={activos}
          escribe={escribe}
          abiertoId={abiertoId}
          alAbrir={(id) => cambiarParametros({ abierto: id, medidor: null })}
          medidorId={medidorId}
          alElegirMedidor={(id) => cambiarParametros({ medidor: id })}
        />
      ) : !cargandoTipo ? (
        <div className="panel">
          <div className="panel-cuerpo">
            <Vacio
              titulo={tipoActivoId ? 'Este tipo no tiene activos' : 'Sin activos elegidos'}
              detalle={
                tipoActivoId
                  ? 'Elige otro tipo de activo.'
                  : 'Elige un tipo de activo para ver sus medidores, o escanea el QR de una máquina.'
              }
            />
          </div>
        </div>
      ) : null}

      {creandoParaTipo ? (
        <FormularioMedidorPorTipo
          tipoActivoId={tipoActivoId}
          alCerrar={() => setCreandoParaTipo(false)}
          alGuardar={() => setCreandoParaTipo(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Todos los activos de un tipo, pidiendo páginas de 100 (el máximo que admite
 * el listado) hasta traerlos completos.
 */
async function activosDelTipo(tipoActivoId: string): Promise<Activo[]> {
  const todos: Activo[] = [];
  for (let pagina = 1; ; pagina++) {
    const respuesta = await api.get<Pagina<Activo>>(
      `/activos${consulta({ pagina, tamano: 100, tipoActivoId, orden: 'nombre' })}`,
    );
    todos.push(...respuesta.datos);
    if (respuesta.datos.length === 0 || todos.length >= respuesta.total) break;
  }
  return todos;
}

/** Crea un medidor con el mismo nombre en cada activo del tipo elegido. */
function FormularioMedidorPorTipo({
  tipoActivoId,
  alCerrar,
  alGuardar,
}: {
  tipoActivoId: string;
  alCerrar: () => void;
  alGuardar: () => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const [nombre, setNombre] = useState('');

  const crear = useMutation({
    mutationFn: () => api.post<{ creados: number }>('/medidores/por-tipo', { nombre, tipoActivoId }),
    onSuccess: async () => {
      await cliente.invalidateQueries();
      alGuardar();
    },
  });

  return (
    <Dialogo titulo="Nuevo medidor para todo el tipo" alCerrar={alCerrar}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (nombre.trim()) crear.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="campo-nota" style={{ margin: 0 }}>
          Se crea un medidor con este nombre en cada activo del tipo seleccionado (sin activos eliminados ni momentáneos).
        </p>

        <div className="campo">
          <label htmlFor="medidor-tipo-nombre">Nombre</label>
          <input id="medidor-tipo-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus required />
        </div>

        {crear.isError ? <ErroresFormulario error={crear.error} /> : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="submit" className="boton" disabled={crear.isPending || !nombre.trim()}>
            Crear en todos
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * Resumen de los activos elegidos: indicadores arriba, una fila por activo con
 * sus medidores, la última lectura y si está dentro de rango. Al abrir un
 * activo se despliega debajo su detalle (medidores y gráfica).
 */
function TablaActivosMedidores({
  activos,
  escribe,
  abiertoId,
  alAbrir,
  medidorId,
  alElegirMedidor,
}: {
  activos: Activo[];
  escribe: boolean;
  abiertoId: string | null;
  alAbrir: (id: string | null) => void;
  medidorId: string | null;
  alElegirMedidor: (id: string | null) => void;
}): JSX.Element {
  const consultas = useQueries({
    queries: activos.map((a) => ({
      queryKey: ['medidores', a.id],
      queryFn: () => api.get<Medidor[]>(`/medidores${consulta({ activoId: a.id })}`),
    })),
  });

  const cargando = consultas.some((q) => q.isLoading);
  const abierto = activos.find((a) => a.id === abiertoId) ?? null;

  // Filas ya calculadas: medidores, última lectura y estado de cada activo.
  const filas = activos.map((a, i) => {
    const medidores = consultas[i]?.data ?? [];
    const lecturas = medidores.flatMap((m) => (m.ultimaLectura ? [m.ultimaLectura] : []));
    const ultima = [...lecturas].sort((x, y) => y.fechaHora.localeCompare(x.fechaHora))[0] ?? null;
    const fueraDeRango = medidores.some(
      (m) =>
        m.ultimaLectura !== null &&
        ((m.valorMinimoEsperado !== null && m.ultimaLectura.valor < m.valorMinimoEsperado) ||
          (m.valorMaximoEsperado !== null && m.ultimaLectura.valor > m.valorMaximoEsperado)),
    );
    const estado: { tono: 'gris' | 'verde' | 'roja'; texto: string } =
      medidores.length === 0
        ? { tono: 'gris', texto: 'Sin medidores' }
        : lecturas.length === 0
          ? { tono: 'gris', texto: 'Sin lecturas' }
          : fueraDeRango
            ? { tono: 'roja', texto: 'Fuera de rango' }
            : { tono: 'verde', texto: 'En rango' };
    return { activo: a, medidores: medidores.length, ultima, estado };
  });

  const totalMedidores = filas.reduce((suma, f) => suma + f.medidores, 0);
  const fueraDeRango = filas.filter((f) => f.estado.tono === 'roja').length;
  const sinLecturas = filas.filter((f) => f.estado.texto === 'Sin lecturas').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="rejilla-indicadores">
        <Indicador titulo="ACTIVOS" valor={activos.length} nota="Elegidos para ver" />
        <Indicador
          titulo="MEDIDORES"
          valor={cargando ? '…' : totalMedidores}
          nota={totalMedidores === 0 && !cargando ? 'Aún no hay medidores' : undefined}
        />
        <Indicador
          titulo="FUERA DE RANGO"
          valor={cargando ? '…' : fueraDeRango}
          nota={fueraDeRango > 0 ? 'Revisar la última lectura' : 'Todo dentro de rango'}
          tono={fueraDeRango > 0 ? 'roja' : 'verde'}
        />
        <Indicador titulo="SIN LECTURAS" valor={cargando ? '…' : sinLecturas} tono="gris" />
      </div>

      <div className="panel">
        <div className="panel-cabecera tono-ot">
          <h2>Activos</h2>
          <span className="campo-nota">Elige uno para ver sus medidores y su gráfica</span>
        </div>
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Activo</th>
                <th>Ubicación</th>
                <th className="ranking-num">Medidores</th>
                <th>Última lectura</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filas.map(({ activo: a, medidores, ultima, estado }, i) => {
                const esAbierto = a.id === abiertoId;
                return (
                  <tr
                    key={a.id}
                    className={esAbierto ? 'ranking-fila' : undefined}
                    onClick={() => alAbrir(esAbierto ? null : a.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span className="numero-registro">#{a.numero}</span>{' '}
                      <strong>{a.nombre}</strong>
                    </td>
                    <td>{a.ubicacionNombre ?? '—'}</td>
                    <td className="ranking-num">
                      {consultas[i]?.isLoading ? (
                        '…'
                      ) : (
                        <span className={`ranking-pill ${medidores > 0 ? 'ranking-pill-ot' : 'ranking-pill-cero'}`}>{medidores}</span>
                      )}
                    </td>
                    <td>
                      {ultima ? (
                        <>
                          <strong>{ultima.valor}</strong>
                          <div style={{ fontSize: 12, color: 'var(--texto-tenue)' }}>{formatearFechaHora(ultima.fechaHora)}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <Etiqueta tono={estado.tono}>{estado.texto}</Etiqueta>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className={esAbierto ? 'boton secundario pequeno' : 'boton pequeno'}>
                        {esAbierto ? 'Cerrar' : 'Ver medidores'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {abierto ? (
        <Dialogo titulo={`#${abierto.numero} ${abierto.nombre}`} ancho={920} alCerrar={() => alAbrir(null)}>
          <DetalleActivoMedidores
            key={abierto.id}
            activo={abierto}
            escribe={escribe}
            medidorId={medidorId}
            alElegirMedidor={alElegirMedidor}
          />
        </Dialogo>
      ) : null}
    </div>
  );
}

/** Medidores de un activo: se elige uno con las pestañas y se ve su gráfica. */
function DetalleActivoMedidores({
  activo,
  escribe,
  medidorId,
  alElegirMedidor,
}: {
  activo: Activo;
  escribe: boolean;
  medidorId: string | null;
  alElegirMedidor: (id: string | null) => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const [creando, setCreando] = useState(false);

  const { data: medidores, isLoading } = useQuery({
    queryKey: ['medidores', activo.id],
    queryFn: () => api.get<Medidor[]>(`/medidores${consulta({ activoId: activo.id })}`),
  });

  const medidor = medidores?.find((m) => m.id === medidorId) ?? medidores?.[0] ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Una sola fila: medidores como pestañas a la izquierda, acciones a la derecha. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          borderBottom: '1px solid var(--borde)',
          paddingBottom: 10,
        }}
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {medidores && medidores.length > 1
            ? medidores.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`boton pequeno${m.id === medidor?.id ? '' : ' secundario'}`}
                  aria-pressed={m.id === medidor?.id}
                  onClick={() => alElegirMedidor(m.id)}
                >
                  {m.nombre}
                </button>
              ))
            : null}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="boton secundario pequeno" to={`/activos/${activo.id}`}>
            Ver ficha del activo
          </Link>
          {escribe ? (
            <button type="button" className="boton pequeno" onClick={() => setCreando(true)}>
              + Nuevo medidor
            </button>
          ) : null}
        </div>
      </div>

      {isLoading ? <Cargando /> : null}


      {medidor ? (
        <MedidorDetalle medidor={medidor} escribe={escribe} />
      ) : !isLoading ? (
        <div className="panel">
          <div className="panel-cuerpo">
            <Vacio
              titulo="Este activo no tiene medidores"
              detalle={escribe ? 'Crea uno para empezar a registrar mediciones.' : 'Pide a un administrador que cree uno.'}
            />
          </div>
        </div>
      ) : null}

      {creando ? (
        <FormularioMedidor
          activoId={activo.id}
          alCerrar={() => setCreando(false)}
          alGuardar={(id) => {
            setCreando(false);
            alElegirMedidor(id);
            void cliente.invalidateQueries({ queryKey: ['medidores', activo.id] });
          }}
        />
      ) : null}
    </div>
  );
}

function MedidorDetalle({ medidor, escribe }: { medidor: Medidor; escribe: boolean }): JSX.Element {
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [registrando, setRegistrando] = useState(false);
  const [editando, setEditando] = useState<LecturaMedidor | null>(null);
  const [eliminando, setEliminando] = useState<LecturaMedidor | null>(null);

  const { data: lecturas, isLoading } = useQuery({
    queryKey: ['medidor-lecturas', medidor.id],
    queryFn: () => api.get<LecturaMedidor[]>(`/medidores/${medidor.id}/lecturas`),
  });

  // Más antigua primero (mismo orden que devuelve el backend): así el N° es
  // estable y corresponde al orden real de registro, sin importar que la
  // tabla de abajo se muestre invertida (más reciente arriba).
  const numeroPorLectura = new Map((lecturas ?? []).map((l, i) => [l.id, i + 1]));

  const invalidar = () =>
    Promise.all([
      cliente.invalidateQueries({ queryKey: ['medidor-lecturas', medidor.id] }),
      cliente.invalidateQueries({ queryKey: ['medidores'] }),
      cliente.invalidateQueries({ queryKey: ['mediciones-activo'] }),
    ]);

  const eliminar = useMutation({
    mutationFn: (lecturaId: string) => api.delete(`/medidores/${medidor.id}/lecturas/${lecturaId}`),
    onSuccess: async () => {
      setEliminando(null);
      await invalidar();
    },
  });

  const tendencia = calcularTendencia(lecturas ?? []);

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>{medidor.nombre}</h2>
        <span style={{ color: 'var(--texto-tenue)', fontSize: 13 }}>{medidor.unidadMedida}</span>
      </div>
      <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="rejilla-indicadores">
          <Indicador titulo="ÚLTIMA LECTURA" valor={medidor.ultimaLectura ? medidor.ultimaLectura.valor : '—'} nota={medidor.ultimaLectura ? formatearFechaHora(medidor.ultimaLectura.fechaHora) : 'Sin lecturas'} />
          <Indicador
            titulo="TENDENCIA"
            valor={tendencia === null ? '—' : tendencia > 0 ? `↑ +${tendencia.toFixed(1)}` : tendencia < 0 ? `↓ ${tendencia.toFixed(1)}` : 'Estable'}
          />
          <Indicador
            titulo="PRÓXIMA LECTURA"
            valor={medidor.proximaLectura ? formatearFecha(medidor.proximaLectura) : '—'}
            className="col-ocultar-movil"
          />
        </div>

        {isLoading ? (
          <Cargando />
        ) : (lecturas ?? []).length < 2 ? (
          <Vacio titulo="Aún no hay suficientes lecturas para graficar" detalle="Registra al menos dos mediciones para ver la tendencia." />
        ) : (
          <GraficoMediciones
            puntos={lecturas ?? []}
            unidad={medidor.unidadMedida}
            minimoEsperado={medidor.valorMinimoEsperado}
            maximoEsperado={medidor.valorMaximoEsperado}
            alto={260}
          />
        )}

        {escribe ? (
          <div>
            <button type="button" className="boton" onClick={() => setRegistrando(true)}>
              + Registrar medición
            </button>
          </div>
        ) : null}

        {registrando ? (
          <ModalNuevaLectura
            medidor={medidor}
            alCerrar={() => setRegistrando(false)}
            alRegistrar={async () => {
              setRegistrando(false);
              await invalidar();
            }}
          />
        ) : null}

        {editando ? (
          <ModalEditarLectura
            medidor={medidor}
            lectura={editando}
            alCerrar={() => setEditando(null)}
            alGuardar={async () => {
              setEditando(null);
              await invalidar();
            }}
          />
        ) : null}

        {eliminando ? (
          <DialogoConfirmar
            titulo="Anular lectura"
            etiqueta="Anular"
            mensaje={`Se anulará la lectura N.° ${numeroPorLectura.get(eliminando.id)} (${eliminando.valor}${medidor.unidadMedida ? ` ${medidor.unidadMedida}` : ''}). Deja de contar en la gráfica y la tendencia; el registro no se borra, queda como anulado.`}
            cargando={eliminar.isPending}
            error={eliminar.error}
            alCerrar={() => {
              eliminar.reset();
              setEliminando(null);
            }}
            alConfirmar={() => eliminar.mutate(eliminando.id)}
          />
        ) : null}

        {lecturas && lecturas.length > 0 ? (
          <details>
            <summary>Historial completo ({lecturas.length})</summary>
            {esMovil ? (
              <div className="tarjetas-ot" style={{ marginTop: 8 }}>
                {[...lecturas].reverse().map((l) => (
                  <div key={l.id} className="tarjeta-ot">
                    <div className="tarjeta-ot-cuerpo">
                      <div className="tarjeta-ot-cabecera">
                        <span className="numero-registro">N.° {numeroPorLectura.get(l.id)}</span>
                        <span>{formatearFechaHora(l.fechaHora)}</span>
                      </div>
                      <div className="tarjeta-ot-titulo">
                        {l.valor}
                        {l.editadoEl ? <span style={{ color: 'var(--texto-tenue)', fontSize: 12 }}> (editado)</span> : null}
                      </div>
                      <div className="tarjeta-ot-datos">
                        <span>{l.origen}</span>
                        {l.nota ? <span>{l.nota}</span> : null}
                      </div>
                    </div>
                    {escribe ? (
                      <div className="acciones-fila">
                        <button type="button" className="boton-icono" title="Editar" aria-label="Editar" onClick={() => setEditando(l)}>
                          <IconoEditar />
                        </button>
                        <button type="button" className="boton-icono peligro" title="Eliminar" aria-label="Eliminar" onClick={() => setEliminando(l)}>
                          <IconoEliminar />
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <table className="tabla" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>N.°</th>
                    <th>Fecha</th>
                    <th>Valor</th>
                    <th>Origen</th>
                    <th>Nota</th>
                    {escribe ? <th>Acciones</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {[...lecturas].reverse().map((l) => (
                    <tr key={l.id}>
                      <td>{numeroPorLectura.get(l.id)}</td>
                      <td>{formatearFechaHora(l.fechaHora)}</td>
                      <td>
                        {l.valor}
                        {l.editadoEl ? <span style={{ color: 'var(--texto-tenue)', fontSize: 12 }}> (editado)</span> : null}
                      </td>
                      <td>{l.origen}</td>
                      <td>{l.nota ?? '—'}</td>
                      {escribe ? (
                        <td style={{ display: 'flex', gap: 8 }}>
                          <button type="button" className="boton-icono" title="Editar" aria-label="Editar" onClick={() => setEditando(l)}>
                            <IconoEditar />
                          </button>
                          <button type="button" className="boton-icono peligro" title="Eliminar" aria-label="Eliminar" onClick={() => setEliminando(l)}>
                            <IconoEliminar />
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </details>
        ) : null}
      </div>
    </div>
  );
}

/** Pendiente entre las dos últimas lecturas — suficiente para la primera señal de alerta. */
function calcularTendencia(lecturas: LecturaMedidor[]): number | null {
  if (lecturas.length < 2) return null;
  const [previa, ultima] = lecturas.slice(-2);
  return ultima!.valor - previa!.valor;
}

/**
 * §11, alta simplificada: solo el nombre — unidad de medida, tipo y
 * frecuencia de recordatorio quedan con sus valores por omisión del backend
 * (o sin definir) y se pueden completar más adelante desde una edición; no
 * son fricción para dar de alta el medidor de una máquina recién elegida.
 */
function FormularioMedidor({
  activoId,
  alCerrar,
  alGuardar,
}: {
  activoId: string;
  alCerrar: () => void;
  alGuardar: (id: string) => void;
}): JSX.Element {
  const [nombre, setNombre] = useState('');

  const crear = useMutation({
    mutationFn: () => api.post<Medidor>('/medidores', { nombre, activoId }),
    onSuccess: (medidor) => alGuardar(medidor.id),
  });

  return (
    <Dialogo titulo="Nuevo medidor" alCerrar={alCerrar}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (nombre.trim()) crear.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <div className="campo">
          <label htmlFor="nuevo-medidor-nombre">Nombre</label>
          <input
            id="nuevo-medidor-nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            autoFocus
            required
          />
        </div>

        {crear.isError ? <ErroresFormulario error={crear.error} /> : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="submit" className="boton" disabled={crear.isPending || !nombre.trim()}>
            Crear medidor
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * Modal de "Nueva medición" — muestra la máquina que se está midiendo (con su
 * foto, cuando tiene una) para que quede claro sobre qué activo se está
 * registrando el valor antes de confirmar, ya que el operador puede tener
 * varias máquinas similares abiertas en la sesión.
 */
function ModalNuevaLectura({
  medidor,
  alCerrar,
  alRegistrar,
}: {
  medidor: Medidor;
  alCerrar: () => void;
  alRegistrar: () => void;
}): JSX.Element {
  const [valor, setValor] = useState('');
  const [fecha, setFecha] = useState(hoyLiteral());

  const { data: fotos } = useQuery({
    queryKey: ['adjuntos', 'ACTIVO', medidor.activoId],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'ACTIVO', entidadId: medidor.activoId })}`),
  });
  const foto = fotos?.[0];

  const registrar = useMutation({
    mutationFn: () => api.post<LecturaMedidor>(`/medidores/${medidor.id}/lecturas`, { valor: Number(valor), fecha }),
    onSuccess: alRegistrar,
  });

  return (
    <Dialogo titulo="Nueva medición" alCerrar={alCerrar}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valor.trim() && fecha) registrar.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {foto ? (
            <img
              src={foto.url}
              alt={medidor.activoNombre}
              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flex: 'none' }}
            />
          ) : (
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 8,
                background: 'var(--lienzo)',
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                color: 'var(--texto-tenue)',
                textAlign: 'center',
              }}
            >
              Sin foto
            </div>
          )}
          <div>
            <div style={{ fontWeight: 700 }}>{medidor.activoNombre}</div>
            <div style={{ fontSize: 12, color: 'var(--texto-tenue)' }}>{medidor.nombre}</div>
          </div>
        </div>

        {/* En una sola columna, no en fila: el panel del calendario de
            SelectorFecha tiene ancho fijo y se sale de la pantalla en móvil
            si su contenedor es más angosto que ese ancho. */}
        <div className="campo">
          <label htmlFor="lectura-valor">Medición{medidor.unidadMedida ? ` (${medidor.unidadMedida})` : ''}</label>
          <input
            id="lectura-valor"
            type="number"
            step="any"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            autoFocus
            required
          />
        </div>
        <SelectorFecha id="lectura-fecha" etiqueta="Fecha" valor={fecha} alCambiar={setFecha} />

        {registrar.isError ? <ErroresFormulario error={registrar.error} /> : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="submit" className="boton" disabled={registrar.isPending || !valor.trim() || !fecha}>
            Registrar
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/** La fecha del hecho medido no se toca al corregir: solo valor y nota. */
function ModalEditarLectura({
  medidor,
  lectura,
  alCerrar,
  alGuardar,
}: {
  medidor: Medidor;
  lectura: LecturaMedidor;
  alCerrar: () => void;
  alGuardar: () => void;
}): JSX.Element {
  const [valor, setValor] = useState(String(lectura.valor));
  const [nota, setNota] = useState(lectura.nota ?? '');

  const editar = useMutation({
    mutationFn: () =>
      api.put(`/medidores/${medidor.id}/lecturas/${lectura.id}`, { valor: Number(valor), nota: nota.trim() || undefined }),
    onSuccess: alGuardar,
  });

  return (
    <Dialogo titulo={`Editar medición del ${formatearFecha(lectura.fechaHora)}`} alCerrar={alCerrar}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valor.trim()) editar.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div className="campo">
          <label htmlFor="lectura-editar-valor">Medición{medidor.unidadMedida ? ` (${medidor.unidadMedida})` : ''}</label>
          <input
            id="lectura-editar-valor"
            type="number"
            step="any"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            autoFocus
            required
          />
        </div>
        <div className="campo">
          <label htmlFor="lectura-editar-nota">Nota</label>
          <input id="lectura-editar-nota" type="text" value={nota} onChange={(e) => setNota(e.target.value)} />
        </div>

        {editar.isError ? <ErroresFormulario error={editar.error} /> : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="submit" className="boton" disabled={editar.isPending || !valor.trim()}>
            Guardar
          </button>
        </div>
      </form>
    </Dialogo>
  );
}
